import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    MessageSquare,
    User,
    Clock,
    Send,
    History,
    MoreVertical,
    Filter,
    CheckCircle2,
    Search,
    X
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageHeader } from './ui/PageHeader';
import { StatusBadge } from './ui/StatusBadge';

const TicketKanban: React.FC = () => {
    const [tickets, setTickets] = useState<any[]>([]);
    const [activeTicket, setActiveTicket] = useState<any>(null);
    const [comments, setComments] = useState<Record<string, string>>({});
    const statuses = ['open', 'in_progress', 'resolved', 'closed'];

    useEffect(() => { fetchTickets(); }, []);

    const fetchTickets = async () => {
        const res = await api.get('/tickets');
        setTickets(res.data);
    };

    const updateStatus = async (id: string, status: string) => {
        await api.patch(`/tickets/${id}`, { status });
        fetchTickets();
    };

    const addComment = async (ticketId: string) => {
        if (!comments[ticketId]) return;
        await api.post(`/tickets/${ticketId}/logs`, { message: comments[ticketId] });
        setComments({ ...comments, [ticketId]: '' });
        const res = await api.get(`/tickets`);
        setTickets(res.data);
        const updated = res.data.find((t: any) => t.id === ticketId);
        setActiveTicket(updated);
    };

    return (
        <div className="h-[calc(100vh-140px)] flex flex-col space-y-6">
            <PageHeader title="Support Tickets" subtitle="Manage and resolve resident complaints" />

            <div className="flex-1 flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {statuses.map(status => (
                    <div key={status} className="flex-none w-80 flex flex-col bg-slate-100/50 rounded-xl border border-slate-200">
                        <div className="p-4 flex items-center justify-between">
                            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                <div className={cn(
                                    "w-2 h-2 rounded-full",
                                    status === 'open' ? 'bg-red-500' : status === 'resolved' ? 'bg-emerald-500' : 'bg-violet-500'
                                )} />
                                {status.replace('_', ' ')}
                            </h3>
                            <span className="text-[10px] font-bold bg-white px-2 py-0.5 rounded border border-slate-200 text-slate-600">
                                {tickets.filter(t => t.status === status).length}
                            </span>
                        </div>

                        <div className="flex-1 overflow-y-auto p-3 space-y-3">
                            {tickets.filter(t => t.status === status).map(t => (
                                <div
                                    key={t.id}
                                    onClick={() => setActiveTicket(t)}
                                    className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 hover:border-violet-300 transition-all cursor-pointer group"
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <span className="text-[10px] font-mono text-slate-400">#{t.id.slice(0,8)}</span>
                                        <span className={cn(
                                            "text-[8px] font-bold uppercase px-1.5 py-0.5 rounded",
                                            t.source === 'whatsapp' ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-blue-600"
                                        )}>
                                            {t.source}
                                        </span>
                                    </div>
                                    <p className="text-sm font-semibold text-slate-800 line-clamp-2 mb-3">{t.description}</p>
                                    <div className="flex items-center justify-between text-slate-400">
                                        <div className="flex items-center gap-1.5 text-[10px] font-medium">
                                            <User size={12} /> Resident
                                        </div>
                                        <div className="flex items-center gap-1 text-[10px] font-medium">
                                            <MessageSquare size={12} /> {t.logs?.length || 0}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {activeTicket && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => setActiveTicket(null)} />
                    <div className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right">
                        <div className="p-6 border-b border-slate-100">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Ticket Detail</p>
                                    <h2 className="text-lg font-bold text-slate-900 line-clamp-2">{activeTicket.description}</h2>
                                </div>
                                <button onClick={() => setActiveTicket(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
                            </div>
                            <div className="flex gap-2">
                                {statuses.map(s => (
                                    <button
                                        key={s}
                                        onClick={() => updateStatus(activeTicket.id, s)}
                                        className={cn(
                                            "px-3 py-1 rounded-md text-[10px] font-bold uppercase border transition-all",
                                            activeTicket.status === s ? "bg-violet-600 text-white border-violet-600 shadow-sm" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                                        )}
                                    >
                                        {s.replace('_', ' ')}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30 space-y-4">
                            <div className="flex items-center gap-2 text-slate-400 text-[10px] font-bold uppercase tracking-wider mb-2">
                                <History size={14} /> Update History
                            </div>
                            {activeTicket.logs?.map((log: any) => (
                                <div key={log.id} className="flex gap-3">
                                    <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0">
                                        <User size={14} className="text-slate-400" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                                            <div className="flex justify-between mb-1">
                                                <span className="text-[10px] font-bold text-violet-600">STAFF</span>
                                                <span className="text-[9px] text-slate-400">{new Date(log.created_at).toLocaleString()}</span>
                                            </div>
                                            <p className="text-sm text-slate-600">{log.message}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="p-4 border-t border-slate-100">
                            <div className="relative">
                                <input
                                    className="w-full bg-slate-100 border-none rounded-lg py-3 pl-4 pr-12 text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                                    placeholder="Add a comment..."
                                    value={comments[activeTicket.id] || ''}
                                    onChange={e => setComments({ ...comments, [activeTicket.id]: e.target.value })}
                                />
                                <button
                                    onClick={() => addComment(activeTicket.id)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-violet-600 text-white rounded-md hover:bg-violet-700"
                                >
                                    <Send size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TicketKanban;
